<template>
  <DialogFull
    :progress="allowedCandidateFeedBack"
    :data="dialogSettings"
    :steps="{ current: step, total: totalSteps }"
    @close="close"
    @onStepBack="stepBack"
  >
    <template v-slot:dialog.body>
      <div class="container col-lg-8 col-sm-12">
        <Permissioned vertical section="interviews.submit-feedback">
          <InternalNotes
            :feedback="feedback"
            permission="internal-notes"
            ref="internalNotes"
            v-show="shouldStepViewBeShown(1)"
            @onChange="updateData"
          />
          <CandidateFeedback
            :feedback="feedback"
            permission="candidate-feedback"
            v-if="shouldStepViewBeShown(2) || ifStepHasAlreadyBeenCompleted(2)"
            v-show="shouldStepViewBeShown(2)"
            @onChange="updateData"
          />
        </Permissioned>
      </div>
    </template>
    <template v-slot:dialog.action>
      <PrimaryButton v-if="step !== totalSteps" @click.native="stepForward">
        Continue
      </PrimaryButton>
      <PrimaryButton @click.native="handleSubmit" :loading="isLoading" v-else>
        Save Feedback
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import InternalNotes from "@/views/interviews/dialogs/SubmitFeedback/steps/InternalNotes";
import CandidateFeedback from "@/views/interviews/dialogs/SubmitFeedback/steps/CandidateFeedback";
import {
  ADD_INTERVIEW_FEEDBACK,
  EDIT_INTERVIEW_FEEDBACK,
  FETCH_INTERVIEW_SLOT
} from "@/store/modules/bookings/interviews/actions";
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { createNamespacedHelpers } from "vuex";
import { copyState } from "@/utils/helpers";
import { addSuccessMessage } from "@/services/utils";
import { INTERVIEW_SLOTS } from "@/models/relationships";
import Permissioned from "@/components/common/Permissioned";
import { isPermissioned } from "@/utils/permissions";

const { mapActions, mapState } = createNamespacedHelpers(INTERVIEWS_NAMESPACE);

export default {
  name: "SubmitFeedbackDialog",
  components: {
    DialogFull,
    PrimaryButton,
    InternalNotes,
    CandidateFeedback,
    Permissioned
  },
  props: {
    isOpen: Boolean,
    editMode: Boolean,
    feedback: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      step: 1,
      savedStep: 1,
      feedbackData: this.feedback,
      isLoading: false
    };
  },
  computed: {
    ...mapState(copyState(["currentInterviewSlot"])),
    currentInterviewId() {
      return this.$route.params.interviewId;
    },
    progressValue() {
      return (100 * this.step) / this.totalSteps;
    },
    dialogSettings() {
      return {
        title: "Interview Feedback",
        dialog: this.isOpen
      };
    },
    allowedCandidateFeedBack() {
      return isPermissioned("interviews.submit-feedback.candidate-feedback");
    },
    totalSteps() {
      return this.allowedCandidateFeedBack ? 2 : 1;
    }
  },
  methods: {
    ...mapActions({
      addInterviewFeedback: ADD_INTERVIEW_FEEDBACK,
      editInterviewFeedback: EDIT_INTERVIEW_FEEDBACK,
      fetchInterviewSlot: FETCH_INTERVIEW_SLOT
    }),
    updateData(feedbackData) {
      this.feedbackData = { ...this.feedbackData, ...feedbackData };
    },
    stepBack() {
      if (this.step > 1) {
        this.step -= 1;
      }
    },
    async stepForward() {
      let isStepFormValid = true;
      if (this.step === 1) {
        isStepFormValid = await this.$refs.internalNotes.validateForm();
      }
      if (isStepFormValid) {
        this.step += 1;
        if (this.savedStep < this.totalSteps && this.step > this.savedStep) {
          this.savedStep += 1;
        }
      }
    },
    close() {
      this.$emit("close");
    },
    shouldStepViewBeShown(viewStep) {
      return this.step === viewStep;
    },
    ifStepHasAlreadyBeenCompleted(viewStep) {
      return viewStep <= this.savedStep;
    },
    async handleSubmit() {
      let feedbackResponseData;
      this.isLoading = true;
      if (this.editMode) {
        feedbackResponseData = await this.onEditInterviewFeedback();
      } else {
        feedbackResponseData = await this.onAddInterviewFeedback();
      }
      await this.$refs.internalNotes.handleFile(feedbackResponseData.id);
      addSuccessMessage();
      this.fetchInterviewSlot(this.currentInterviewId);
      this.close();
      this.isLoading = false;
    },
    async onAddInterviewFeedback() {
      const { data } = await this.addInterviewFeedback({
        ...this.feedbackData,
        interviewSlot: {
          id: this.currentInterviewSlot.id,
          type: INTERVIEW_SLOTS
        }
      });
      return data;
    },
    async onEditInterviewFeedback() {
      const { candidateFeedback, internalNotes } = this.feedbackData;
      const { data } = await this.editInterviewFeedback({
        data: {
          candidateFeedback,
          internalNotes
        },
        id: this.feedbackData.id
      });
      return data;
    }
  }
};
</script>

<style scoped lang="scss"></style>
